import { API_BASE, apiFetch } from "../../api/http";

// Structured issue type returned by jurisdiction check
export interface JurisdictionIssue {
  anchor: string;
  analysis: string;
  currentText: string;
  correction: string;
  ruleReference?: string;
  sourceType?: string;
}

export interface JurisdictionCheckResponse {
  analysis: string;  // Bullet-formatted text (for backwards compatibility)
  issues: JurisdictionIssue[];  // Structured issues array
  checkedRequirements?: number;
}

export async function postJurisdictionCheck(body: { query?: string; docContent: string; detectedJurisdiction: string; docType: "protocol" | "protective" }): Promise<JurisdictionCheckResponse>{
  const res = await apiFetch(`${API_BASE}/api/rag/jurisdictionCheck`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => null);
    throw new Error(error?.message || `Jurisdiction Check failed: ${res.status}`);
  }
  return res.json();
}

export async function postJurisdictionCheckStream(
  body: { query?: string; docContent: string; detectedJurisdiction: string; docType: "protocol" | "protective" },
  onChunk: (content: string) => void,
  onComplete?: (tokenUsage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number }) => void,
  onError?: (error: string) => void
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/rag/jurisdictionCheck/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`jurisdictionCheck/stream failed: ${res.status}`);
  }

  const reader = res.body?.getReader();
  if (!reader) {
    throw new Error("No response body reader available");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Process complete SSE events (data: {...}\n\n)
    const events = buffer.split("\n\n");
    buffer = events.pop() || ""; // Keep incomplete event in buffer

    for (const event of events) {
      if (!event.startsWith("data: ")) continue;
      try {
        const data = JSON.parse(event.slice(6));
        if (data.type === "content") {
          onChunk(data.content);
        } else if (data.type === "done") {
          onComplete?.(data.tokenUsage);
        } else if (data.type === "error") {
          onError?.(data.error);
        }
      } catch {
        // Ignore parse errors for incomplete JSON
      }
    }
  }
}

export async function postRagFlawAnalysis(body: {
  query: string;
  docContent?: string;
  jurisdiction?: string;
  docType?: string;
}): Promise<{ analysis: string }>{
  const res = await apiFetch(`${API_BASE}/api/rag/flawAnalysis`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`rag/flawAnalysis failed: ${res.status}`);
  return res.json();
}

// Structured talking point type
export interface TalkingPoint {
  topic: string;
  originalLanguage: string;
  actualEdits: string;
  position: string;
  strategicFallback: string;
}

export interface TalkingPointsResponse {
  analysis: string;  // Text format (for backwards compatibility)
  talkingPoints: TalkingPoint[];  // Structured talking points array
}

export async function postTalkingPoints(body: {
  prompt?: string;
  firstDraftSnapshot?: string;
  currentDraftSnapshot?: string;
  jurisdiction?: string;
  docType?: string;
}): Promise<TalkingPointsResponse>{
  const res = await apiFetch(`${API_BASE}/api/rag/talkingPoints`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(errBody.analysis || errBody.message || `rag/talkingPoints failed: ${res.status}`);
  }
  return res.json();
}

